import { provideAbpCore, withOptions, AuthService, EnvironmentService, AuthErrorFilter } from '@abp/ng.core';
import { provideAbpOAuth, OAuthErrorFilterService } from '@abp/ng.oauth';
import { provideSettingManagementConfig } from '@abp/ng.setting-management/config';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import { provideAbpThemeShared,} from '@abp/ng.theme.shared';
import { provideIdentityConfig } from '@abp/ng.identity/config';
import { provideAccountConfig } from '@abp/ng.account/config';
import { provideTenantManagementConfig } from '@abp/ng.tenant-management/config';
import { registerLocaleForEsBuild } from '@abp/ng.core/locale';
import { provideThemeLeptonX } from '@abp/ng.theme.lepton-x';
import { provideSideMenuLayout } from '@abp/ng.theme.lepton-x/layouts';
import { provideLogo, withEnvironmentOptions } from "@abp/ng.theme.shared";
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { ApplicationConfig, LOCALE_ID, inject, provideAppInitializer } from '@angular/core';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideRouter } from '@angular/router';
import { environment } from '../environments/environment';
import { APP_ROUTES } from './app.routes';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { FOOTER_PROVIDER } from './footer/footer.config';
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { SingleFlightOAuthService } from './core/auth/single-flight-oauth.service';
import { connectNotificationHub } from './core/signals/notification.signal';
import { ThemeService } from './core/services/theme.service';

const DD_MM_YYYY_FORMATS = {
  parse: { dateInput: 'dd/MM/yyyy' },
  display: { dateInput: 'dd/MM/yyyy', monthYearLabel: 'MMM yyyy', dateA11yLabel: 'dd/MM/yyyy', monthYearA11yLabel: 'MMM yyyy' },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),
    FOOTER_PROVIDER,
    provideAnimations(),
    provideCharts(withDefaultRegisterables()),
    provideAbpCore(
      withOptions({
        environment,
        registerLocaleFn: registerLocaleForEsBuild(),
      }),
    ),
    provideAppInitializer(() => {
      inject(OAuthErrorFilterService).add({
        id: 'mallflow-skip-token-validation-error',
        executable: true,
        execute: (event: OAuthErrorEvent) => event.type === 'token_validation_error',
      } satisfies AuthErrorFilter<OAuthErrorEvent>);
    }),
    provideAbpOAuth(),
    { provide: OAuthService, useClass: SingleFlightOAuthService },
    provideIdentityConfig(),
    provideSettingManagementConfig(),
    provideFeatureManagementConfig(),
    provideThemeLeptonX(),
    provideSideMenuLayout(),
    provideLogo(withEnvironmentOptions(environment)),
    provideAccountConfig(),
    provideTenantManagementConfig(),
    provideAbpThemeShared(),
    APP_ROUTE_PROVIDER,
    { provide: LOCALE_ID, useValue: 'en-US' },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMATS },
    provideNativeDateAdapter(),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      const env = inject(EnvironmentService);
      if (auth.isAuthenticated) {
        connectNotificationHub(auth.getAccessToken(), env.getApiUrl('default'));
      }
    }),
    provideAppInitializer(() => {
      inject(ThemeService);
    }),
  ]
};
